// Stav přihlášení (F1 1.1): kdo je přihlášený se zjišťuje výhradně z GET /ja (docs/08-autorizace.md
// §5.3). Relace je v HttpOnly cookie, kterou nastavil backend po návratu z Entra ID — frontend
// žádný token nedrží a nic nerozhoduje sám; jen kreslí to, co server vrátil.
//
// Stavy: `nacitam` (čeká se na /ja) · `prihlasen` (200, profil v `ja`) · `neprihlasen` (401)
// · `chyba` (jiná chyba — síť, 5xx; UI ukáže důvod a nabídne „Zkusit znovu“).
import type { Ja } from '@ecoser/shared';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { KLIC_JA, odhlasitNaServeru, urlPrihlaseni, useJa } from '../api/ja';
import { ChybaApi, naChybu, type Chyba } from '../api/klient';
import {
  jaVPameti,
  nactiJaZTabletu,
  ulozJaDoTabletu,
  zapomenJaZTabletu,
} from '../offline/ja-tabletu';
import { zapomenoutPristani } from '../shell/pristani';

export type StavPrihlaseni = 'nacitam' | 'prihlasen' | 'neprihlasen' | 'chyba';

export interface PrihlaseniKontext {
  stav: StavPrihlaseni;
  /** Profil z GET /ja — jen ve stavu `prihlasen`, jinak null. */
  ja: Ja | null;
  /** Důvod, proč se nepodařilo přihlášení ověřit — jen ve stavu `chyba`. */
  chyba: Chyba | null;
  /** Probíhá (opakovaný) dotaz na /ja — pro neaktivní tlačítko „Zkusit znovu“. */
  overuji: boolean;
  /** Znovu se zeptat serveru (po chybě). */
  zkusitZnovu: () => void;
  /**
   * Přesměruje prohlížeč na backend (`/api/v1/auth/prihlaseni?navrat=…`), ten dál na Microsoft.
   * `navrat` = relativní cesta v aplikaci, kam se po přihlášení vrátit (výchozí `/`).
   */
  prihlasit: (navrat?: string) => void;
  /**
   * Zneplatní relaci na serveru, vymaže profil z cache a přesměruje prohlížeč na odhlášení
   * u Microsoftu (`odhlaseni_url`), jinak na /prihlaseni. Chybu (kromě 401) vyhodí volajícímu.
   */
  odhlasit: () => Promise<void>;
}

const Kontext = createContext<PrihlaseniKontext | null>(null);

export function PoskytovatelPrihlaseni({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const dotaz = useJa();
  const { refetch } = dotaz;
  // Profil uložený v tabletu (4.5) — bez signálu se `GET /ja` nezeptá a řidič by po restartu tabletu
  // uprostřed směny skončil na hlášce „Nepodařilo se ověřit přihlášení“ s frontou zápisů v tabletu.
  const [zTabletu, setZTabletu] = useState<Ja | undefined>(jaVPameti);
  useEffect(() => {
    let zivy = true;
    void nactiJaZTabletu().then((ja) => {
      if (zivy && ja) setZTabletu(ja);
    });
    return () => {
      zivy = false;
    };
  }, []);
  useEffect(() => {
    if (dotaz.data) ulozJaDoTabletu(dotaz.data);
  }, [dotaz.data]);

  // Výpadek sítě (status 0) je jediná chyba, kterou uložený profil zaskočí; 5xx ani 401 ne — server
  // odpověděl a jeho slovo platí (08 §5.3).
  const bezSignalu =
    dotaz.isError &&
    dotaz.error instanceof ChybaApi &&
    dotaz.error.status === 0 &&
    Boolean(zTabletu);

  let stav: StavPrihlaseni;
  if (dotaz.isPending) {
    stav = 'nacitam';
  } else if (bezSignalu) {
    stav = 'prihlasen';
  } else if (dotaz.isError) {
    stav = 'chyba';
  } else if (dotaz.data) {
    stav = 'prihlasen';
  } else {
    stav = 'neprihlasen';
  }

  const zkusitZnovu = useCallback(() => {
    void refetch();
  }, [refetch]);

  const prihlasit = useCallback((navrat = '/') => {
    window.location.assign(urlPrihlaseni(navrat));
  }, []);

  const odhlasit = useCallback(async () => {
    let cil = '/prihlaseni';
    try {
      const odpoved = await odhlasitNaServeru();
      if (odpoved?.odhlaseni_url) {
        cil = odpoved.odhlaseni_url;
      }
    } catch (chyba) {
      // Relace už neplatí (401) → jen dokončit odhlášení v prohlížeči; jinou chybu ukáže UI.
      if (!(chyba instanceof ChybaApi && chyba.status === 401)) {
        throw chyba;
      }
    }
    // Profil pryč z cache → aplikace je hned „nepřihlášen“; prohlížeč pak stránku stejně opustí.
    // Příští přihlášení má zase přistát podle role (08 §5.4 bod 5).
    zapomenoutPristani();
    await zapomenJaZTabletu();
    queryClient.setQueryData<Ja | null>(KLIC_JA, null);
    window.location.assign(cil);
  }, [queryClient]);

  const ja = stav === 'prihlasen' ? (dotaz.data ?? zTabletu ?? null) : null;
  const chyba = stav === 'chyba' ? naChybu(dotaz.error) : null;
  const overuji = dotaz.isFetching;

  const hodnota = useMemo<PrihlaseniKontext>(
    () => ({ stav, ja, chyba, overuji, zkusitZnovu, prihlasit, odhlasit }),
    [stav, ja, chyba, overuji, zkusitZnovu, prihlasit, odhlasit],
  );

  return <Kontext.Provider value={hodnota}>{children}</Kontext.Provider>;
}

export function usePrihlaseni(): PrihlaseniKontext {
  const kontext = useContext(Kontext);
  if (!kontext) {
    throw new Error('usePrihlaseni je nutné volat uvnitř PoskytovatelPrihlaseni.');
  }
  return kontext;
}
